import {
  cloneElement,
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from "react";
import type { DialogBaseProps } from "./DialogBaseProps";

/**
 * Navigation-surviving dialog registry. Opt in by opening dialogs via
 * `<Button dialog={...} tray>` (sugar over `tray.openElement`) or
 * `useDialogTray().open(Component, props)`. Persistence is in-memory across
 * client-side navigation only — full reload rehydration is out of scope.
 *
 * The Dialog primitive intentionally has no `tray` prop: every tray-managed
 * dialog is instantiated by THIS registry (in the host's React tree, above
 * the Router), so its state — form drafts, validation, scroll — lives there
 * too. That's the only way to keep input valueTracker in sync with a single
 * reconciliation cycle (see PR #3208 for the historical desync). Declarative
 * `<Dialog open={state}>` JSX renders a plain modal in the parent's tree
 * with no tray involvement; that's by design.
 */

// Closures (not typed fields) keep each entry point's ergonomic typing in
// scope without a `Record<string, unknown>` cast.
type EntryRender = (
  open: boolean,
  onOpenChange: (open: boolean) => void,
) => ReactNode;

export type DialogTrayEntry = {
  readonly key: string;
  readonly displayName: string;
  readonly parked: boolean;
  readonly render: EntryRender;
  /** Mid Clear-all exit animation; chip is still rendered so it can slide out. */
  readonly closing: boolean;
  /** ms of transition-delay on the chip's slide-out transform. */
  readonly closingDelay: number;
  /** Caller-supplied identity key. Subsequent open*() calls with the
   *  same key collapse to this entry instead of registering a new one. */
  readonly dedupeKey?: string;
  /** Full URL (pathname + search) where the entry was opened. Used as
   *  the navigation target when the operator clicks "← Back to X" —
   *  they land on the exact tab / view state they were on when they
   *  parked the dialog. NOT used for grouping; that's `groupKey`. */
  readonly originPath: string;
  /** Canonical key for grouping parked dialogs together. Derived from
   *  `originPath` by stripping UI-state query params (currently `tab`,
   *  `tab2`, ... — anything the `Tabs` component writes). Two dialogs
   *  parked from different tabs of the same shipment share a
   *  `groupKey` and live in the same group; their `originPath`s
   *  differ so navigating back returns to the right tab. */
  readonly groupKey: string;
  /** Human-readable label of the origin (e.g. "Shipment CANX2VFJ").
   *  Caller-supplied via DialogTrayOpenOptions; falls back to
   *  document.title at open time. Used for the grouping header. */
  readonly originLabel: string;
  /** Opt-in: render the parked chip above modals (at the dialog's own
   *  z-index) instead of behind them. See DialogTrayOpenOptions. */
  readonly chipAboveModals: boolean;
};

export type DialogTrayHandle = {
  readonly key: string;
  close(): void;
  park(): void;
  restore(): void;
  setName(name: string): void;
  setDirty(dirty: boolean): void;
};

/** Stable mutators — never change identity. Subscribers don't re-render
 *  on entries changes. Use in preference to the full api whenever the
 *  caller doesn't actually read entries (Buttons, auto sync). */
/** Options accepted by both `open` and `openElement`. `onClose` fires
 *  exactly once when the entry is removed for any reason — user dismiss,
 *  Clear-all, or programmatic close. Lets callers (e.g. a Button) know
 *  the dialog they registered is gone without subscribing to `entries`. */
export type DialogTrayOpenOptions = {
  name?: string;
  onClose?: () => void;
  /** Caller-supplied identity for the dialog. If an entry with the same
   *  `dedupeKey` is already in the tray, the provider returns its
   *  existing handle instead of registering a duplicate (and restores
   *  it if it was parked). Survives client-side navigation because the
   *  guard lives in the provider, not in a per-trigger ref — the case
   *  that per-button state can't cover is open → park → navigate away →
   *  navigate back → click again: the trigger remounts with no memory,
   *  but the provider still holds the entry. */
  dedupeKey?: string;
  /** Human-readable label of the screen the dialog was opened from
   *  (e.g. "Shipment CANX2VFJ"). Shown in the "← back to X" grouping
   *  header when the user navigates away. Defaults to `document.title`
   *  at open time, which works for most screens but is verbose for
   *  shipment views — explicit is better. */
  originLabel?: string;
  /** When true, this dialog's parked chip renders at the dialog's own
   *  z-index (above any dialog below it in the stack) instead of the
   *  default behind-all-modals z. Opt-in for the "open a child dialog,
   *  park it, keep the parent open and the chip reachable on top of the
   *  parent's overlay" flow. Off by default — parked chips stay behind
   *  modals so a freshly opened dialog covers them. */
  chipAboveModals?: boolean;
};

export type DialogTrayMutators = {
  open<P extends DialogBaseProps>(
    component: ComponentType<P>,
    props: Omit<P, "open" | "onOpenChange">,
    options?: DialogTrayOpenOptions,
  ): DialogTrayHandle;
  openElement(
    element: ReactElement<DialogBaseProps>,
    options?: DialogTrayOpenOptions,
  ): DialogTrayHandle;
  close(key: string): void;
  park(key: string): void;
  restore(key: string): void;
  setName(key: string, name: string): void;
  /** Mark dirty. Tracked in a ref (not state) — reactive dirty would
   *  re-render the host every keystroke, desyncing input valueTracker
   *  (cursor jumps to end; same root cause as PR #3208). Read via
   *  `getDirtyKeys()` at confirm time. */
  setDirty(key: string, dirty: boolean): void;
  getDirtyKeys(): ReadonlySet<string>;
  clearAll(): void;
};

/** Entries-driven slice — changes whenever the registry mutates. Used by
 *  the host and the rail; not by any consumer that just needs to register
 *  or close a dialog. */
export type DialogTrayState = {
  readonly entries: ReadonlyArray<DialogTrayEntry>;
  readonly parkedCount: number;
};

/** Combined api retained for hosts and stories that already read both
 *  pieces. Equivalent to spreading mutators + state. */
export type DialogTrayApi = DialogTrayMutators & DialogTrayState;

const DialogTrayMutatorsContext = createContext<DialogTrayMutators | undefined>(
  undefined,
);
const DialogTrayStateContext = createContext<DialogTrayState | undefined>(
  undefined,
);

// Per-entry context for the dialog tree the host renders. Presence
// signals "I'm tray-managed"; parkedIndex (-1 when not parked) drives
// vertical chip stacking.
export type DialogTrayEntryHandle = {
  readonly key: string;
  readonly parked: boolean;
  readonly parkedIndex: number;
  readonly closing: boolean;
  readonly closingDelay: number;
  /** Full origin URL — used by the header to navigate back. */
  readonly originPath: string;
  /** Canonical key for grouping (originPath with UI-state params like
   *  `tab` stripped). Two dialogs from different tabs of the same
   *  screen share a `groupKey`. */
  readonly groupKey: string;
  /** Human-readable origin label for the grouping header. */
  readonly originLabel: string;
  /** True when `originPath === currentPath`. Computed by the host (which
   *  subscribes to popstate) and passed down so the chip doesn't have to
   *  re-implement the route subscription. */
  readonly isOnOrigin: boolean;
  /** Absolute top CSS for the chip computed by the host. Honours the
   *  collapsed/expanded state of the chip's group (off-current groups
   *  collapse to a small peek offset until their header is hovered).
   *  Undefined for non-parked entries — they render as modals and
   *  don't use this. */
  readonly chipTopCss: string | undefined;
  /** True when this chip's group is currently expanded (somebody is
   *  hovering anywhere in the group's hit-area). Drives the chip's
   *  label expansion in the disabled-for-grouping case: when the
   *  group expands, the chip shows its title so the operator can see
   *  what they parked. False for chips in the active group (their
   *  labels still expand off individual button hover). */
  readonly groupExpanded: boolean;
  /** Opt-in: render the parked chip above modals instead of behind them.
   *  Read by DialogInner to pick the chip's z-index. */
  readonly chipAboveModals: boolean;
};
const DialogTrayEntryContext = createContext<DialogTrayEntryHandle | undefined>(
  undefined,
);

/**
 * Hover mutators for the parked-group rail. Separated from the per-entry
 * context because (a) the value is stable for the host's lifetime — both
 * mutators are `useCallback`s — and (b) only the chip needs them, so
 * threading them through the per-entry context would spuriously invalidate
 * memoization on entry-shape changes. Provider lives inside DialogTrayHost.
 */
export type DialogTrayGroupHover = {
  hoverEnter(groupKey: string): void;
  hoverLeave(): void;
};
const DialogTrayGroupHoverContext = createContext<
  DialogTrayGroupHover | undefined
>(undefined);
export const DialogTrayGroupHoverProvider_ =
  DialogTrayGroupHoverContext.Provider;
export function useDialogTrayGroupHoverOptional():
  | DialogTrayGroupHover
  | undefined {
  return useContext(DialogTrayGroupHoverContext);
}

export function useDialogTray(): DialogTrayApi {
  const m = useContext(DialogTrayMutatorsContext);
  const s = useContext(DialogTrayStateContext);
  if (!m || !s) {
    throw new Error(
      "useDialogTray must be used inside a DialogTrayProvider — wrap your app root with <DialogTrayProvider>.",
    );
  }
  return { ...m, ...s };
}

/** Optional variant — returns undefined when no provider is in scope. */
export function useDialogTrayOptional(): DialogTrayApi | undefined {
  const m = useContext(DialogTrayMutatorsContext);
  const s = useContext(DialogTrayStateContext);
  if (!m || !s) return undefined;
  return { ...m, ...s };
}

/** Mutators only — does NOT re-render on entries changes. Prefer this
 *  whenever the caller doesn't read entries. */
export function useDialogTrayMutatorsOptional():
  | DialogTrayMutators
  | undefined {
  return useContext(DialogTrayMutatorsContext);
}

/** True if an entry with the given dedupeKey is currently in the tray
 *  (and not mid-exit-animation). Subscribes to the entries state, so
 *  callers re-render whenever entries change — the cost is bounded
 *  because entry changes are O(per dialog open/close), not per-keystroke
 *  (setDirty deliberately lives in a ref, see its docstring).
 *
 *  Returns false (and does not require a provider) when `dedupeKey` is
 *  undefined — lets opt-in callers pass through unknown keys without
 *  branching at the call site. */
export function useDialogTrayHasEntry(dedupeKey: string | undefined): boolean {
  const state = useContext(DialogTrayStateContext);
  if (!dedupeKey || !state) return false;
  return state.entries.some((e) => e.dedupeKey === dedupeKey && !e.closing);
}

export function useDialogTrayEntry(): DialogTrayEntryHandle | undefined {
  return useContext(DialogTrayEntryContext);
}

// Internal — DialogTrayHost wraps each rendered entry with this provider.
export function DialogTrayEntryProvider_({
  entryKey,
  parked,
  parkedIndex,
  closing,
  closingDelay,
  originPath,
  groupKey,
  originLabel,
  isOnOrigin,
  chipTopCss,
  groupExpanded,
  chipAboveModals,
  children,
}: {
  entryKey: string;
  parked: boolean;
  parkedIndex: number;
  closing: boolean;
  closingDelay: number;
  originPath: string;
  groupKey: string;
  originLabel: string;
  isOnOrigin: boolean;
  chipTopCss: string | undefined;
  groupExpanded: boolean;
  chipAboveModals: boolean;
  children: ReactNode;
}) {
  const handle = useMemo(
    () => ({
      key: entryKey,
      parked,
      parkedIndex,
      closing,
      closingDelay,
      originPath,
      groupKey,
      originLabel,
      isOnOrigin,
      chipTopCss,
      groupExpanded,
      chipAboveModals,
    }),
    [
      entryKey,
      parked,
      parkedIndex,
      closing,
      closingDelay,
      originPath,
      groupKey,
      originLabel,
      isOnOrigin,
      chipTopCss,
      groupExpanded,
      chipAboveModals,
    ],
  );
  return (
    <DialogTrayEntryContext.Provider value={handle}>
      {children}
    </DialogTrayEntryContext.Provider>
  );
}

/** Cascade timing for the Clear-all slide-out. Must match the chip's
 *  `transition-[transform...]` duration (200ms in `DialogSlideAsideChip_`). */
const CLEAR_ALL_STAGGER_MS = 60;
const CLEAR_ALL_DURATION_MS = 200;

/**
 * Reduce a `pathname + search` URL to the canonical key used for
 * tray grouping. Strips UI-state query params that `Tabs` writes
 * (`tab`, `tab2`, `tab3`, ...) so two dialogs parked from different
 * tabs of the same screen end up in the same group — the operator
 * thinks of them as belonging to the same entity, not the same tab.
 *
 * Exported so `DialogTrayHost` can apply the same stripping to the
 * current URL for the `isOnOrigin` check.
 */
export function canonicalizeGroupKey(pathPlusSearch: string): string {
  const [path = "", search = ""] = pathPlusSearch.split("?", 2) as [
    string,
    string?,
  ];
  if (!search) return path;
  const params = new URLSearchParams(search);
  const toDelete: string[] = [];
  for (const key of params.keys()) {
    if (key === "tab" || /^tab\d+$/.test(key)) toDelete.push(key);
  }
  if (toDelete.length === 0) return pathPlusSearch;
  for (const key of toDelete) params.delete(key);
  const cleaned = params.toString();
  return cleaned ? `${path}?${cleaned}` : path;
}

export function DialogTrayProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<DialogTrayEntry[]>([]);
  // entriesRef mirrors state for ref-stable callbacks (clearAll reads
  // latest); counterRef is per-provider so parallel providers (tests)
  // get independent key sequences; dirtyKeysRef is non-reactive (setDirty).
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const counterRef = useRef(0);
  const dirtyKeysRef = useRef<Set<string>>(new Set());
  // Per-entry onClose callbacks set via openElement/open options. Stored
  // in a ref so registering one doesn't bump entries-subscribed renders.
  // Fired exactly once when the entry is removed, then deleted.
  const onCloseRef = useRef<Map<string, () => void>>(new Map());

  const close = useCallback((key: string) => {
    const cb = onCloseRef.current.get(key);
    onCloseRef.current.delete(key);
    dirtyKeysRef.current.delete(key);
    setEntries((prev) => {
      if (!prev.some((e) => e.key === key)) return prev;
      return prev.filter((e) => e.key !== key);
    });
    cb?.();
  }, []);

  // Bail on no-op so subscribers (DialogTrayHost, Clear-all rail) don't
  // re-render when park() is called on an entry that's already parked —
  // same pattern as setName. Without the bail, map() returns a fresh
  // array reference and React would propagate the wasted render.
  const park = useCallback((key: string) => {
    setEntries((prev) => {
      const target = prev.find((e) => e.key === key);
      if (!target || target.parked) return prev;
      return prev.map((e) => (e.key === key ? { ...e, parked: true } : e));
    });
  }, []);

  const restore = useCallback((key: string) => {
    setEntries((prev) => {
      const target = prev.find((e) => e.key === key);
      if (!target || !target.parked) return prev;
      return prev.map((e) => (e.key === key ? { ...e, parked: false } : e));
    });
  }, []);

  // Idempotent — auto-name effect calls on every render so bail when
  // value matches; otherwise every render churns entries.
  const setName = useCallback((key: string, name: string) => {
    setEntries((prev) => {
      const target = prev.find((e) => e.key === key);
      if (!target || target.displayName === name) return prev;
      return prev.map((e) => (e.key === key ? { ...e, displayName: name } : e));
    });
  }, []);

  const setDirty = useCallback((key: string, dirty: boolean) => {
    if (dirty) dirtyKeysRef.current.add(key);
    else dirtyKeysRef.current.delete(key);
  }, []);

  const getDirtyKeys = useCallback(
    (): ReadonlySet<string> => dirtyKeysRef.current,
    [],
  );

  const clearAll = useCallback(() => {
    // Two-phase cascade: mark parked `closing` with staggered delay (chip
    // CSS animates translateX(100%) with that delay), filter out after
    // total animation. Non-parked entries (active modal) untouched.
    const parked = entriesRef.current.filter((e) => e.parked && !e.closing);
    if (parked.length === 0) return;
    const parkedKeys = new Set(parked.map((e) => e.key));
    setEntries((prev) =>
      prev.map((e) => {
        if (!parkedKeys.has(e.key)) return e;
        const idx = parked.findIndex((p) => p.key === e.key);
        return {
          ...e,
          closing: true,
          closingDelay: idx * CLEAR_ALL_STAGGER_MS,
        };
      }),
    );
    const totalMs =
      (parked.length - 1) * CLEAR_ALL_STAGGER_MS + CLEAR_ALL_DURATION_MS + 50;
    setTimeout(() => {
      // Collect onClose callbacks before mutating maps so we can fire them
      // after the state update lands — same close-then-notify ordering as
      // the single-entry close() path.
      const callbacks: Array<() => void> = [];
      for (const entry of parked) {
        dirtyKeysRef.current.delete(entry.key);
        const cb = onCloseRef.current.get(entry.key);
        if (cb) {
          onCloseRef.current.delete(entry.key);
          callbacks.push(cb);
        }
      }
      setEntries((prev) => prev.filter((e) => !parkedKeys.has(e.key)));
      for (const cb of callbacks) cb();
    }, totalMs);
  }, []);

  // Stable mutators captured by handles returned from open()/openElement().
  // Without this ref, a handle stored in a button's onClick closure would
  // call a stale mutator after re-renders.
  const mutatorsRef = useRef({ close, park, restore, setName, setDirty });
  mutatorsRef.current = { close, park, restore, setName, setDirty };

  const handleFor = useCallback((key: string): DialogTrayHandle => {
    const m = mutatorsRef.current;
    return {
      key,
      close: () => m.close(key),
      park: () => m.park(key),
      restore: () => m.restore(key),
      setName: (n: string) => m.setName(key, n),
      setDirty: (d: boolean) => m.setDirty(key, d),
    };
  }, []);

  const registerEntry = useCallback(
    (
      render: EntryRender,
      name: string,
      onClose?: () => void,
      dedupeKey?: string,
      originLabel?: string,
      chipAboveModals = false,
    ): DialogTrayHandle => {
      // Dedup path: if an entry with this dedupeKey is already alive,
      // return a handle pointing at it instead of creating a duplicate.
      // Update its onClose to the new callback — the original opener
      // may have unmounted (cross-navigation re-click), and only the
      // current caller knows what to do when the entry eventually
      // closes. Restore the entry if it was parked so the click visibly
      // does something.
      if (dedupeKey) {
        const existing = entriesRef.current.find(
          (e) => e.dedupeKey === dedupeKey && !e.closing,
        );
        if (existing) {
          if (onClose) onCloseRef.current.set(existing.key, onClose);
          else onCloseRef.current.delete(existing.key);
          if (existing.parked) {
            setEntries((prev) =>
              prev.map((e) =>
                e.key === existing.key ? { ...e, parked: false } : e,
              ),
            );
          }
          return handleFor(existing.key);
        }
      }
      counterRef.current += 1;
      const key = `dtray-${counterRef.current}`;
      // Capture the path at registration time. We deliberately don't
      // re-capture on park()/restore() — the conceptual origin is where
      // the dialog was opened (i.e. where the form's context lives, see
      // "Form owns its mutations" in hubble/CLAUDE.md). Even if the user
      // parks → navigates → restores → parks again, the dialog should
      // still belong to its original screen.
      //
      // Two URL forms are kept:
      //   - originPath: pathname + search exactly as it was — used for
      //     navigating back so the user lands on the same tab.
      //   - groupKey:   originPath with UI-state params (tab*) stripped
      //     — used purely for grouping, so dialogs parked from
      //     different tabs of the same screen end up in one group.
      const originPath =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "";
      const groupKey = canonicalizeGroupKey(originPath);
      // Default label: take the part of `document.title` before any
      // em-dash separator (Hubble's title format is "Page — Hubble").
      // Callers should pass `originLabel` explicitly for nice copy, but
      // the fallback shouldn't carry the app name as noise. `||` (not
      // `??`) so that an empty `document.title` — page still loading,
      // or title is only the separator — falls through to the literal
      // "previous page", not an empty "← Back to " header.
      const resolvedOriginLabel =
        originLabel ||
        (typeof document !== "undefined"
          ? document.title.split(/\s+[—–-]\s+/)[0]
          : undefined) ||
        "previous page";
      const entry: DialogTrayEntry = {
        key,
        displayName: name,
        parked: false,
        render,
        closing: false,
        closingDelay: 0,
        originPath,
        groupKey,
        originLabel: resolvedOriginLabel,
        chipAboveModals,
        ...(dedupeKey ? { dedupeKey } : {}),
      };
      if (onClose) onCloseRef.current.set(key, onClose);
      setEntries((prev) => [...prev, entry]);
      return handleFor(key);
    },
    [handleFor],
  );

  const open = useCallback(
    <P extends DialogBaseProps>(
      component: ComponentType<P>,
      props: Omit<P, "open" | "onOpenChange">,
      options?: DialogTrayOpenOptions,
    ): DialogTrayHandle => {
      const render: EntryRender = (open, onOpenChange) =>
        createElement(component, { ...props, open, onOpenChange } as P);
      return registerEntry(
        render,
        options?.name ?? "Dialog",
        options?.onClose,
        options?.dedupeKey,
        options?.originLabel,
        options?.chipAboveModals,
      );
    },
    [registerEntry],
  );

  const openElement = useCallback(
    (
      element: ReactElement<DialogBaseProps>,
      options?: DialogTrayOpenOptions,
    ): DialogTrayHandle => {
      const render: EntryRender = (open, onOpenChange) =>
        cloneElement(element, { open, onOpenChange });
      return registerEntry(
        render,
        options?.name ?? "Dialog",
        options?.onClose,
        options?.dedupeKey,
        options?.originLabel,
        options?.chipAboveModals,
      );
    },
    [registerEntry],
  );

  const mutatorsValue = useMemo<DialogTrayMutators>(
    () => ({
      open,
      openElement,
      close,
      park,
      restore,
      setName,
      setDirty,
      getDirtyKeys,
      clearAll,
    }),
    [
      open,
      openElement,
      close,
      park,
      restore,
      setName,
      setDirty,
      getDirtyKeys,
      clearAll,
    ],
  );

  // State value: changes whenever entries do. Only DialogTrayHost and the
  // global Clear-all need this. Dirty state is intentionally NOT here —
  // see `setDirty` for the cursor-jump-regression rationale.
  const stateValue = useMemo<DialogTrayState>(() => {
    const parkedCount = entries.reduce((n, e) => n + (e.parked ? 1 : 0), 0);
    return { entries, parkedCount };
  }, [entries]);

  return (
    <DialogTrayMutatorsContext.Provider value={mutatorsValue}>
      <DialogTrayStateContext.Provider value={stateValue}>
        {children}
      </DialogTrayStateContext.Provider>
    </DialogTrayMutatorsContext.Provider>
  );
}
